/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * ClientMessagesTab - Coach view for sending messages/announcements to a specific client
 * Part of P3: Coach Announcements feature
 */

import { useState, useEffect } from 'react'
import {
  Bell,
  Send,
  Loader2,
  Megaphone,
  Calendar,
  CheckCircle,
  Clock,
  Edit2,
  Trash2,
  AlertCircle,
  Plus
} from 'lucide-react'
import coachService from '../services/coachService'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('ClientMessagesTab')

const ClientMessagesTab = ({ clientId, clientName, permissions }) => {
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [showComposeForm, setShowComposeForm] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  })

  // Check if coach has permission to send messages
  const canSendMessages = permissions?.canSendMessages !== false

  useEffect(() => {
    loadAnnouncements()
  }, [clientId])

  const loadAnnouncements = async () => {
    try {
      setLoading(true)
      // Get all announcements and filter to those that include this client
      const response = await coachService.getAnnouncements({ limit: 50 })
      if (response.success) {
        // Filter announcements that were sent to all clients or specifically to this client
        const clientAnnouncements = response.announcements.filter(
          (a) =>
            a.audience === 'all' || (a.audience === 'specific' && a.targetIds?.includes(clientId))
        )
        setAnnouncements(clientAnnouncements)
      }
    } catch (error) {
      logger.error('Error loading announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async (e) => {
    e.preventDefault()
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in both title and message')
      return
    }

    try {
      setSending(true)
      const response = await coachService.sendAnnouncementToClient(clientId, {
        title: formData.title.trim(),
        content: formData.content.trim()
      })

      if (response.success) {
        toast.success('Announcement sent successfully')
        setFormData({ title: '', content: '' })
        setShowComposeForm(false)
        loadAnnouncements()
      }
    } catch (error) {
      logger.error('Error sending announcement:', error)
      toast.error(error.response?.data?.message || 'Failed to send announcement')
    } finally {
      setSending(false)
    }
  }

  const handleDelete = async (announcementId) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return

    try {
      await coachService.deleteAnnouncement(announcementId)
      toast.success('Announcement deleted')
      setAnnouncements(announcements.filter((a) => a.id !== announcementId))
    } catch (error) {
      logger.error('Error deleting announcement:', error)
      toast.error('Failed to delete announcement')
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    }
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  // Check if student has read the announcement
  const isReadByClient = (announcement) => {
    return announcement.reads?.some((r) => r.user?.id === clientId || r.userId === clientId)
  }

  if (!canSendMessages) {
    return (
      <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
          <AlertCircle className="w-5 h-5" />
          <p className="font-medium">Messaging not permitted</p>
        </div>
        <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
          The student has limited your messaging permissions. Contact them to request additional
          access.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Compose Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Announcements to {clientName}
          </h3>
        </div>
        {!showComposeForm && (
          <button onClick={() => setShowComposeForm(true)} className="btn btn-primary btn-sm">
            <Plus className="w-4 h-4 mr-1" />
            New Announcement
          </button>
        )}
      </div>

      {/* Compose Form */}
      {showComposeForm && (
        <form onSubmit={handleSend} className="card">
          <div className="card-body space-y-4">
            <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
              <Megaphone className="w-5 h-5" />
              <h4 className="font-medium">Compose Announcement</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter message subject..."
                maxLength={200}
                className="input w-full"
                disabled={sending}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.title.length}/200</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Message
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your message here..."
                rows={5}
                maxLength={10000}
                className="input w-full resize-none"
                disabled={sending}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.content.length}/10,000</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowComposeForm(false)
                  setFormData({ title: '', content: '' })
                }}
                className="btn btn-secondary"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending || !formData.title.trim() || !formData.content.trim()}
                className="btn btn-primary"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Announcements List */}
      {announcements.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No announcements sent yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            Send an announcement to {clientName}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <div key={announcement.id} className="card border-l-4 border-l-primary-500">
              <div className="card-body">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {announcement.title}
                      </h4>
                      {announcement.audience === 'all' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                          Sent to all
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {announcement.content}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(announcement.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete message"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(announcement.publishedAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {isReadByClient(announcement) ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Read
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        Unread
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ClientMessagesTab
